import {computed, effect, Inject, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {SearchSuggestItem} from '../../../types/SearchSuggestItem';
import {
  SearchLabelEnum,
  SearchPlaceLabelEnum,
  SearchPlaceTypeNameEnum,
  SearchSuggestTypesEnum,
} from '../../../enums/SearchEnums';
import {SearchSuggestItemData} from '../../../types/SearchSuggestItemData';
import {EnumsService} from '../../../services/enums.service';
import {SearchWidgetV3StateService} from './search-widget-v3-state.service';
import {SearchService} from '../../../services/search/search.service';
import {SearchResultContent} from '../../../types/SearchResultContent';
import {SearchPreprocessQuery} from '../../../types/SearchPreprocessQuery';
import {RoutesEnums} from '../../../enums/RoutesEnums';
import {QueryParams} from '../../../types/QueryParams';
import {UrlTermsSymbolsEnum} from '../../../enums/SeoEnums';
import {Router} from '@angular/router';
import {NoScrollService} from '../../../services/no-scroll.service';
import {SearchWidgetLabelsEnum} from '../../search-widget-v2/enums/search-widget-labels.enum';
import {SearchPreprocessResponse} from '../../../types/SearchPreprocessResponse';

@Injectable({
  providedIn: 'root',
})
export class SearchWidgetV3ManagerService {
  private enumsService = inject(EnumsService);
  private searchWidgetV3StateService = inject(SearchWidgetV3StateService);
  private isSearchWidgetExpanded = this.searchWidgetV3StateService.isSearchWidgetExpanded;
  private mobilePlacesPanelOpen = this.searchWidgetV3StateService.mobilePlacesPanelOpen;
  private mobileCategoriesPanelOpen = this.searchWidgetV3StateService.mobileCategoriesPanelOpen;
  private searchService = inject(SearchService);
  private searchState = this.searchService.searchState;
  private searchResults = this.searchService.searchResults;
  private searchResultsContent = computed<SearchResultContent>(() => {
    return this.searchResults() ? this.searchResults().searchResultContent : null;
  });
  private widgetState = inject(SearchWidgetV3StateService);
  private router = inject(Router);
  private noScrollService = inject(NoScrollService);
  private previousCareServiceTypeName: string = null;
  private previousCaregiverUnitTypeName: string = null;
  private previousPlaceName: string = null;
  private previousSymptomName: string = null;
  private previousFreeSearchTerm: string = null;
  private previousLocationSearch: boolean = null;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    effect(
      () => {
        if (this.isSearchWidgetExpanded() || this.mobilePlacesPanelOpen() || this.mobileCategoriesPanelOpen()) {
          this.noScrollService.setScrollBlocked(true);
        } else {
          this.noScrollService.setScrollBlocked(false);
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        const searchResultsContent = this.searchResultsContent();
        const searchState = this.searchState();
        if (searchResultsContent && searchState) {
          if (
            searchResultsContent?.careServiceType?.name !== this.previousCareServiceTypeName ||
            searchResultsContent?.caregiverUnitType?.name !== this.previousCaregiverUnitTypeName ||
            searchResultsContent?.place?.name !== this.previousPlaceName ||
            searchResultsContent?.symptom?.name !== this.previousSymptomName ||
            searchResultsContent?.freeText !== this.previousFreeSearchTerm ||
            searchState.locationSearch !== this.previousLocationSearch
          ) {
            this.previousCareServiceTypeName = searchResultsContent?.careServiceType?.name;
            this.previousCaregiverUnitTypeName = searchResultsContent?.caregiverUnitType?.name;
            this.previousPlaceName = searchResultsContent?.place?.name || '';
            this.previousSymptomName = searchResultsContent?.symptom?.name;
            this.previousFreeSearchTerm = searchResultsContent?.freeText;
            this.previousLocationSearch = searchState.locationSearch;

            const newFreeSearchFieldValueArray: string[] = [];
            let newFreeSearchFieldValue: string = '';
            if (this.previousCareServiceTypeName) newFreeSearchFieldValueArray.push(this.previousCareServiceTypeName);
            if (this.previousCaregiverUnitTypeName)
              newFreeSearchFieldValueArray.push(this.previousCaregiverUnitTypeName);
            if (this.previousSymptomName) newFreeSearchFieldValueArray.push(this.previousSymptomName);
            if (this.previousFreeSearchTerm) newFreeSearchFieldValueArray.push(this.previousFreeSearchTerm);

            if (newFreeSearchFieldValueArray.length > 0) {
              newFreeSearchFieldValue = newFreeSearchFieldValueArray.join(', ');
            }

            this.widgetState.updateFreeSearchFieldValue(newFreeSearchFieldValue);
            this.widgetState.updateNewFreeSearchFieldValue(newFreeSearchFieldValue);

            if (searchState.locationSearch === true) {
              this.widgetState.updatePlacesFieldValue(SearchWidgetLabelsEnum.NARA_MIG);
              this.widgetState.updateNewPlacesFieldValue('');
            } else {
              this.widgetState.updatePlacesFieldValue(this.previousPlaceName);
              this.widgetState.updateNewPlacesFieldValue(this.previousPlaceName);
            }

            if (newFreeSearchFieldValue !== '' || searchState.locationSearch === true) {
              this.widgetState.updateIsLocationSearchVisible(true);
            } else {
              this.widgetState.updateIsLocationSearchVisible(false);
            }
          }
        }
      },
      {allowSignalWrites: true},
    );
  }

  public onFreeSearchFieldEnterHit(term: string): void {
    this.performPreprocessSearch(term, this.searchWidgetV3StateService.newPlacesFieldValue());
  }

  public onPlaceFieldEnterHit(term: string): void {
    this.performPreprocessSearch(this.searchWidgetV3StateService.newFreeSearchFieldValue(), term);
  }

  public onSearchButtonClick(): void {
    const freeSearchTerm = this.searchWidgetV3StateService.newFreeSearchFieldValue();
    const placeTerm = this.searchWidgetV3StateService.newPlacesFieldValue();
    this.performPreprocessSearch(freeSearchTerm, placeTerm);
  }

  public loadPlaces(): void {
    this.searchService.reloadPlaces();
  }

  public getSuggestTypeLabel(suggestItem: SearchSuggestItem): string {
    let typeLabel = '';
    if (suggestItem.searchValue.length === 1) {
      const type: string = suggestItem.searchValue[0].type;
      const enumKey: string = this.enumsService.getEnumKeyByEnumValue(SearchSuggestTypesEnum, type);
      if (enumKey == 'PLACE' && suggestItem.searchValue[0]) {
        typeLabel = this.getSuggestTypeLabelForPlace(suggestItem.searchValue[0].data);
      } else {
        typeLabel = SearchLabelEnum[enumKey];
      }
    }
    return typeLabel;
  }

  public getUserLocation(): void {
    if (navigator.geolocation) {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000,
      };
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.widgetState.updateLocationGetErrorMessage(null);
          this.widgetState.updateIsSearchWidgetExpanded(false);
          this.widgetState.updateIsLocationLoading(false);
          const resultContentWithoutPlace = this.getSearchResultsContentWithoutPlace();
          const routerLink = this.buildSearchRouterLinkFromSearchResultContent(resultContentWithoutPlace);
          const queryParams = this.buildSearchQueryParamsFromSearchResultContent(resultContentWithoutPlace);
          queryParams[UrlTermsSymbolsEnum.LATITUDE] = position.coords.latitude;
          queryParams[UrlTermsSymbolsEnum.LONGITUDE] = position.coords.longitude;
          this.router.navigate(routerLink, {queryParams});
        },
        (error) => this.handleLocationError(error),
        options,
      );
    } else {
      this.handleLocationError(null);
    }
  }

  private performPreprocessSearch(freeSearchTerm: string, placeTerm: string): void {
    const query: SearchPreprocessQuery = {
      freeSearchTerm: freeSearchTerm,
      placeTerm: placeTerm,
    };
    this.searchService.preprocessSearch(query).subscribe({
      next: (response: SearchPreprocessResponse) => {
        this.doRedirectToTheSearchResultsPage(response?.searchResultContent);
        this.widgetState.updateIsSearchWidgetExpanded(false);
      },
      error: (error) => {
        console.error('Error during preprocess search:', error);
        this.router.navigate(['/', RoutesEnums.NOTFOUND]);
        this.widgetState.updateIsSearchWidgetExpanded(false);
      },
    });
  }

  private doRedirectToTheSearchResultsPage(searchResultContent: SearchResultContent) {
    if (searchResultContent) {
      const routerLink: string[] = this.buildSearchRouterLinkFromSearchResultContent(searchResultContent);
      const queryParams: QueryParams = this.buildSearchQueryParamsFromSearchResultContent(searchResultContent);
      this.router.navigate(routerLink, {queryParams});
    } else {
      this.router.navigate(['/', RoutesEnums.NOTFOUND]);
    }
  }

  private buildSearchRouterLinkFromSearchResultContent(searchResultContent: SearchResultContent): string[] {
    const routerLink: string[] = ['/', RoutesEnums.SEARCH, RoutesEnums.SEARCH_VARDGIVARE];
    if (searchResultContent?.freeText) {
      return [...routerLink, RoutesEnums.SEARCH_VARDGIVARE];
    }
    if (searchResultContent?.caregiverUnitType) {
      routerLink.push(`${searchResultContent.caregiverUnitType.urlPath}-${UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE}`);
    }
    if (searchResultContent?.place) {
      routerLink.push(`${searchResultContent.place.urlPath}-${UrlTermsSymbolsEnum.PLACE}`);
    }
    if (searchResultContent?.symptom) {
      routerLink.push(`${searchResultContent.symptom.urlPath}-${UrlTermsSymbolsEnum.SYMPTOM}`);
    }
    if (searchResultContent?.careServiceType) {
      routerLink.push(`${searchResultContent.careServiceType.urlPath}-${UrlTermsSymbolsEnum.SERVICE_TYPE}`);
    }
    return routerLink;
  }

  private buildSearchQueryParamsFromSearchResultContent(searchResultContent: SearchResultContent): QueryParams {
    const queryParams: QueryParams = {};
    if (searchResultContent?.freeText) {
      if (searchResultContent.caregiverUnitType) {
        queryParams[UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE] = searchResultContent.caregiverUnitType.urlPath;
      }
      queryParams[UrlTermsSymbolsEnum.FREE] = searchResultContent.freeText;
      if (searchResultContent.place) {
        queryParams[UrlTermsSymbolsEnum.PLACE] = searchResultContent.place.urlPath;
      }
      if (searchResultContent.symptom) {
        queryParams[UrlTermsSymbolsEnum.SYMPTOM] = searchResultContent.symptom.urlPath;
      }
      if (searchResultContent.careServiceType) {
        queryParams[UrlTermsSymbolsEnum.SERVICE_TYPE] = searchResultContent.careServiceType.urlPath;
      }
    }
    return queryParams;
  }

  private getSuggestTypeLabelForPlace(data: string): string {
    const dataOb: SearchSuggestItemData = JSON.parse(data);
    switch (dataOb.TypeName) {
      case SearchPlaceTypeNameEnum.COUNTY:
        return SearchPlaceLabelEnum.COUNTY;
      case SearchPlaceTypeNameEnum.MUNICIPALITY:
        return SearchPlaceLabelEnum.MUNICIPALITY;
      case SearchPlaceTypeNameEnum.DISTRICT:
        return SearchPlaceLabelEnum.DISTRICT;
      default:
        return SearchPlaceLabelEnum.DEFAULT;
    }
  }

  private getSearchResultsContentWithoutPlace(): SearchResultContent {
    if (this.searchResultsContent()) {
      return {
        ...this.searchResultsContent(),
        place: null,
      };
    }
    return null;
  }

  private handleLocationError(error: GeolocationPositionError): void {
    this.widgetState.updateIsLocationLoading(false);

    let errorMsg = '';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMsg = 'Användaren nekade begäran om platsinformation.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMsg = 'Platsinformation är inte tillgänglig.';
        break;
      case error.TIMEOUT:
        errorMsg = 'Begäran om användarens platsinformation tog för lång tid.';
        break;
      default:
        errorMsg = 'Platsinformation stöds inte av denna webbläsare.';
        break;
    }

    this.widgetState.updateLocationGetErrorMessage(errorMsg);
    setTimeout(() => {
      this.widgetState.updateLocationGetErrorMessage(null);
    }, 10000);
  }
}
